import { TextArea } from '@consigli/facade';
import { FC, useCallback, useRef, useState } from 'react';

interface InlineEditableTextProps {
  initialValue: string;
  onCommit: (text: string) => void | Promise<unknown>;
  onCancel: () => void;
  className?: string;
}

export const autoSize = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

export const InlineEditableText: FC<InlineEditableTextProps> = ({
  initialValue,
  onCommit,
  onCancel,
  className = '',
}) => {
  const [draft, setDraft] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const textareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    autoSize(el);
    el.focus();
  }, []);

  const commit = useCallback(async () => {
    if (isSavingRef.current) return;

    const trimmed = draft.trim();
    if (!trimmed || trimmed === initialValue) {
      onCancel();
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await onCommit(trimmed);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
      onCancel();
    }
  }, [draft, initialValue, onCommit, onCancel]);

  return (
    <TextArea
      ref={textareaRef}
      value={draft}
      rows={1}
      disabled={isSaving}
      onChange={(e) => setDraft(e.target.value)}
      onInput={(e) => autoSize(e.currentTarget)}
      onClick={(e) => e.stopPropagation()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          // Let blur do the commit so both paths behave identically.
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          onCancel();
        }
      }}
      className={`text-sm resize-none overflow-hidden ${isSaving ? 'opacity-60' : ''} ${className}`}
    />
  );
};
